import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface InterfaceValidationSelectionState {
    selectAll: boolean;
    selectedIds: string[];
    excludedIds: string[];
}

@Injectable({
    providedIn: 'root',
})
export class InterfaceValidationSelectionService {
    private readonly selectionStateSubject = new BehaviorSubject<InterfaceValidationSelectionState>(
        this.emptySelectionState(),
    );

    readonly selectionState$: Observable<InterfaceValidationSelectionState> =
        this.selectionStateSubject.asObservable();

    get selectionState(): InterfaceValidationSelectionState {
        const { selectAll, selectedIds, excludedIds } = this.selectionStateSubject.value;
        return {
            selectAll,
            selectedIds: [...selectedIds],
            excludedIds: [...excludedIds],
        };
    }

    setSelectedIds(ormIds: readonly string[]): void {
        this.selectionStateSubject.next({
            selectAll: false,
            selectedIds: [...new Set(ormIds)],
            excludedIds: [],
        });
    }

    selectAll(): void {
        this.selectionStateSubject.next({
            selectAll: true,
            selectedIds: [],
            excludedIds: [],
        });
    }

    addSelectedId(ormId: string): void {
        const state = this.selectionStateSubject.value;
        if (state.selectAll) {
            this.selectionStateSubject.next({
                ...state,
                excludedIds: state.excludedIds.filter((excludedOrmId) => excludedOrmId !== ormId),
            });
            return;
        }

        if (!state.selectedIds.includes(ormId)) {
            this.selectionStateSubject.next({
                ...state,
                selectedIds: [...state.selectedIds, ormId],
            });
        }
    }

    removeSelectedId(ormId: string): void {
        const state = this.selectionStateSubject.value;
        if (state.selectAll) {
            if (!state.excludedIds.includes(ormId)) {
                this.selectionStateSubject.next({
                    ...state,
                    excludedIds: [...state.excludedIds, ormId],
                });
            }
            return;
        }

        this.selectionStateSubject.next({
            ...state,
            selectedIds: state.selectedIds.filter((selectedOrmId) => selectedOrmId !== ormId),
        });
    }

    clearSelectedIds(): void {
        this.selectionStateSubject.next(this.emptySelectionState());
    }

    updateVisibleSelection(
        availableOrmIds: readonly string[],
        selectedOrmIds: readonly string[],
    ): void {
        const state = this.selectionStateSubject.value;
        if (!state.selectAll) {
            this.setSelectedIds(selectedOrmIds);
            return;
        }

        const selectedIdSet = new Set(selectedOrmIds);
        const excludedIdSet = new Set(state.excludedIds);
        availableOrmIds.forEach((ormId) => {
            if (selectedIdSet.has(ormId)) {
                excludedIdSet.delete(ormId);
            } else {
                excludedIdSet.add(ormId);
            }
        });
        this.selectionStateSubject.next({
            ...state,
            excludedIds: [...excludedIdSet],
        });
    }

    isSelected(ormId: string): boolean {
        const state = this.selectionStateSubject.value;
        return state.selectAll
            ? !state.excludedIds.includes(ormId)
            : state.selectedIds.includes(ormId);
    }

    private emptySelectionState(): InterfaceValidationSelectionState {
        return {
            selectAll: false,
            selectedIds: [],
            excludedIds: [],
        };
    }
}
